import React from 'react';
import { Clock, Database } from 'lucide-react';

interface BackfillProgressBarProps {
  backfillStatus: {
    sensor_id: string;
    total_entries: number;
    acknowledged: number;
    remaining: number;
    progress_percent: number;
    timeframe_days: number;
    timeframe_hours: number;
    created_at: string;
    updated_at: string;
    seconds_since_update: number;
  };
}

export const BackfillProgressBar: React.FC<BackfillProgressBarProps> = ({ backfillStatus }) => {
  const { acknowledged, total_entries, remaining, updated_at, timeframe_days, timeframe_hours } = backfillStatus;
  const progress_percent = Number(backfillStatus.progress_percent);
  
  // Debug logging removed for performance
  
  // Check if the backfill is active (updated within last 3 minutes) and not complete
  const isActive = () => {
    const updateTime = new Date(updated_at).getTime();
    const now = Date.now();
    const threeMinutesAgo = now - (3 * 60 * 1000);
    return updateTime > threeMinutesAgo;
  };

  const active = isActive();
  const isComplete = progress_percent >= 100;
  const hasSignificantRemaining = remaining > 3;

  if (!active || isComplete || !hasSignificantRemaining) {
    return null; // Don't show progress bar if not active, complete, or less than 3 remaining
  }

  const formatTimeframe = () => {
    if (timeframe_days > 0) {
      return `${timeframe_days}d ${timeframe_hours}h`;
    }
    return `${timeframe_hours}h`;
  };

  return (
    <div className="flex flex-col space-y-1 min-w-0">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center space-x-1">
          <Database className="h-3 w-3 text-blue-500" />
          <span className="text-blue-600 dark:text-blue-400 font-medium">Backfill</span>
        </div>
        <span className="text-gray-500 dark:text-gray-400">{progress_percent.toFixed(1)}%</span>
      </div>
      
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div 
          className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.min(progress_percent, 100)}%` }}
        />
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{acknowledged.toLocaleString()} / {remaining.toLocaleString()}</span>
        <div className="flex items-center space-x-1">
          <Clock className="h-3 w-3" />
          <span>{formatTimeframe()}</span>
        </div>
      </div>
    </div>
  );
};
